// Listing block (Views/Partials/pagination/PaginatedContentGrid.cshtml, and DocumentListing.cshtml
// for the Document Hub):
// - a changed filter, sort or View archive submits the form, back to page 1 (no page button is
//   the submitter);
// - the events' month list highlights the month in view;
// - the events' search opens from its button and closes again (story 50457);
// - back on the Document Hub from Login, the file they clicked downloads (story 50483).

export function initListings(): void {
  document.querySelectorAll<HTMLFormElement>('[data-listing-form]').forEach((form) => {
    form.querySelectorAll<HTMLSelectElement | HTMLInputElement>('[data-listing-filter]').forEach((control) => {
      control.addEventListener('change', () => form.requestSubmit());
    });
  });

  document.querySelectorAll<HTMLElement>('[data-event-months]').forEach(initEventMonths);
  document.querySelectorAll<HTMLElement>('[data-expandable-search]').forEach(initExpandableSearch);

  const autoDownload = document.querySelector<HTMLElement>('[data-auto-download]')?.dataset.autoDownload;
  if (autoDownload) {
    // Drop ?download= first, so a reload or a shared link doesn't download it again.
    const url = new URL(window.location.href);
    url.searchParams.delete('download');
    history.replaceState(history.state, '', url);

    // The file comes as an attachment, so the hub stays on screen.
    window.location.assign(autoDownload);
  }
}

function initExpandableSearch(search: HTMLElement): void {
  const openButton = search.querySelector<HTMLButtonElement>('[data-expandable-search-open]');
  const closeButton = search.querySelector<HTMLButtonElement>('[data-expandable-search-close]');
  const input = search.querySelector<HTMLInputElement>('input[type="search"]');
  if (!openButton || !closeButton || !input) {
    return;
  }

  // A search that's on keeps the box open, showing what was searched for.
  const searchedFor = input.value.trim();

  const setOpen = (open: boolean): void => {
    search.classList.toggle('is-collapsed', !open);
    openButton.setAttribute('aria-expanded', String(open));
  };

  // Closed straight away, not shrinking shut as the page loads.
  if (!searchedFor) {
    search.style.transition = 'none';
    setOpen(false);
    void search.offsetWidth;
    search.style.transition = '';
  }

  openButton.addEventListener('click', () => {
    setOpen(true);
    input.focus();
  });

  // Closing ends the search too: with one on, the listing reloads without it.
  const close = (): void => {
    input.value = '';
    if (searchedFor) {
      // Disabled, it's left out of the URL rather than sent empty.
      input.disabled = true;
      input.form?.requestSubmit();
      return;
    }
    setOpen(false);
    openButton.focus();
  };

  closeButton.addEventListener('click', close);
  // Back to this page from the back/forward cache, the box works again.
  window.addEventListener('pageshow', () => {
    input.disabled = false;
  });
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      close();
    }
  });
}

function initEventMonths(nav: HTMLElement): void {
  const links = Array.from(nav.querySelectorAll<HTMLAnchorElement>('a[href^="#"]'));
  const groups = links
    .map((link) => document.getElementById(decodeURIComponent(link.hash.slice(1))))
    .filter((group): group is HTMLElement => group !== null);

  if (groups.length === 0) {
    return;
  }

  const setActive = (id: string): void => {
    links.forEach((link) => {
      const active = link.hash === `#${id}`;
      link.classList.toggle('active', active);
      if (active) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  // The month in view is the last one whose top has passed a third of the way down the screen.
  const update = (): void => {
    const line = window.innerHeight / 3;
    let current = groups[0];
    for (const group of groups) {
      if (group.getBoundingClientRect().top <= line) {
        current = group;
      }
    }
    setActive(current.id);
  };

  let queued = false;
  window.addEventListener('scroll', () => {
    if (!queued) {
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        update();
      });
    }
  }, { passive: true });

  update();
}
